import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStaff } from "@/context/StaffContext";
import api, { apiError } from "@/lib/api";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  LifeBuoy, LogOut, ShieldCheck, ShieldAlert, CheckCircle2, ScrollText, Users,
  Activity, RefreshCw, DollarSign, BarChart3, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { APP_NAME } from "@/lib/branding";
import { BrandLogo } from "@/components/BrandLogo";
import { planLabel } from "@/lib/plans";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import StaffFinancials from "./StaffFinancials";
import StaffClients from "./StaffClients";
import StaffInactivity from "./StaffInactivity";

function fmt(iso) {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function StaffPortal() {
  const { t } = useTranslation();
  const { staff, logout } = useStaff();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAudit, setOpenAudit] = useState(null); // tenant obj
  const [auditData, setAuditData] = useState(null);
  const [alerts, setAlerts] = useState({ alerts: [], open_count: 0, recent_runs: [] });
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t1, a1] = await Promise.all([
        api.get("/staff/tenants"),
        api.get("/staff/chain-alerts"),
      ]);
      setTenants(t1.data.tenants);
      setAlerts(a1.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/staff/chain-check/run-now");
      const b = data.breakages?.length || 0;
      if (b === 0) toast.success(`${data.tenants_checked} tenant(s) vérifié(s) — aucune altération`);
      else toast.error(`${b} altération(s) détectée(s)`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setScanning(false); }
  };

  const acknowledge = async (alertId) => {
    try {
      await api.post("/staff/chain-alerts/ack", { alert_id: alertId });
      toast.success(t("staff.alerts.acknowledged"));
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const viewAudit = async (tenant) => {
    setOpenAudit(tenant);
    setAuditData(null);
    try {
      const { data } = await api.get(`/staff/tenants/${tenant.id}/audit-log`);
      setAuditData(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="staff-portal">
      <header className="h-16 sticky top-0 z-30 bg-slate-950/85 backdrop-blur border-b border-slate-800 px-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BrandLogo variant="full" className="h-9" />
          <span className="h-5 w-px bg-white/20" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-amber-400">
            Staff
          </span>
          <Badge className="ml-2 bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/10">
            2FA · TOTP
          </Badge>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-400">{staff?.email}</div>
          <Button variant="ghost"
                  onClick={async () => { await logout(); navigate("/staff/login"); }}
                  data-testid="staff-logout-btn"
                  className="text-slate-300 hover:text-white hover:bg-slate-900">
            <LogOut className="h-4 w-4 mr-1" /> {t("common.signOut")}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 sm:p-10 space-y-6">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-amber-400">
            {t("staff.title")}
          </div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Back-office {APP_NAME}
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Chaque consultation ci-dessous est journalisée dans le tenant concerné.
          </p>
        </div>

        <Tabs defaultValue="tenants" data-testid="staff-tabs">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="tenants" data-testid="tab-tenants" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white"><ShieldCheck className="w-3.5 h-3.5 mr-1" />Tenants</TabsTrigger>
            <TabsTrigger value="financials" data-testid="tab-financials" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white"><DollarSign className="w-3.5 h-3.5 mr-1" />Financier</TabsTrigger>
            <TabsTrigger value="clients" data-testid="tab-clients" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white"><Users className="w-3.5 h-3.5 mr-1" />Clients</TabsTrigger>
            <TabsTrigger value="inactivity" data-testid="tab-inactivity" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white"><AlertCircle className="w-3.5 h-3.5 mr-1" />Inactivité</TabsTrigger>
          </TabsList>

          <TabsContent value="financials" className="mt-4">
            <StaffFinancials />
          </TabsContent>
          <TabsContent value="clients" className="mt-4">
            <StaffClients />
          </TabsContent>
          <TabsContent value="inactivity" className="mt-4">
            <StaffInactivity />
          </TabsContent>

          <TabsContent value="tenants" className="mt-4 space-y-6">
        {/* Chain integrity monitor */}
        <Card className="border-slate-800 bg-slate-900 rounded-xl p-5" data-testid="chain-monitor-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                alerts.open_count > 0 ? "bg-red-500/10" : "bg-green-500/10"
              }`}>
                {alerts.open_count > 0
                  ? <ShieldAlert className="h-5 w-5 text-red-400" />
                  : <ShieldCheck className="h-5 w-5 text-green-400" />}
              </div>
              <div>
                <div className="text-sm font-medium text-slate-100">{t("staff.alerts.title")}</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {alerts.open_count > 0
                    ? <span className="text-red-300" data-testid="alerts-open-badge">
                        {t("staff.alerts.openAlerts", { n: alerts.open_count })}
                      </span>
                    : <span className="text-green-300" data-testid="alerts-clear-badge">
                        {t("staff.alerts.allClear")}
                      </span>}
                  {alerts.recent_runs?.[0] && (
                    <span className="ml-2 text-slate-500">
                      · {t("staff.alerts.lastRun")}: {fmt(alerts.recent_runs[0].finished_at)}
                      {" · "}{t("staff.alerts.tenantsChecked", { n: alerts.recent_runs[0].tenants_checked })}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={runScan} disabled={scanning}
                    data-testid="run-chain-scan-btn"
                    className="border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white bg-transparent">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${scanning ? "animate-spin" : ""}`} />
              {t("staff.alerts.runNow")}
            </Button>
          </div>

          {alerts.alerts.filter((a) => a.status === "open").length > 0 && (
            <div className="mt-4 space-y-2" data-testid="chain-alerts-list">
              {alerts.alerts.filter((a) => a.status === "open").map((a) => (
                <div key={a.id} className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 flex items-center gap-3"
                     data-testid={`chain-alert-${a.id}`}>
                  <ShieldAlert className="h-4 w-4 text-red-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-100">{a.tenant_name}</div>
                    <div className="text-xs text-slate-400">
                      {t("staff.alerts.brokenAt", { seq: a.broken_at })} · {t("staff.alerts.reason")}: {a.reason || "—"} · {fmt(a.timestamp)}
                    </div>
                  </div>
                  <Button size="sm" variant="outline"
                          data-testid={`ack-alert-${a.id}`}
                          onClick={() => acknowledge(a.id)}
                          className="border-red-500/30 text-red-200 hover:bg-red-500/10 bg-transparent">
                    {t("staff.alerts.acknowledge")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="border-slate-800 bg-slate-900 overflow-hidden rounded-xl">
          {loading ? (
            <div className="p-10 text-center text-slate-400 text-sm">{t("common.loading")}</div>
          ) : (
            <Table data-testid="staff-tenants-table">
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400">{t("staff.col.name")}</TableHead>
                  <TableHead className="text-slate-400">{t("staff.col.plan")}</TableHead>
                  <TableHead className="text-slate-400">{t("staff.col.members")}</TableHead>
                  <TableHead className="text-slate-400">{t("staff.col.created")}</TableHead>
                  <TableHead className="text-right text-slate-400"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((t2) => (
                  <TableRow key={t2.id} className="border-slate-800 hover:bg-slate-800/40"
                            data-testid={`staff-tenant-row-${t2.name}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-slate-500" />
                        <div>
                          <div className="text-slate-100">{t2.name}</div>
                          {t2.siren && <div className="text-[10px] uppercase tracking-widest text-slate-500">SIREN {t2.siren}</div>}
                        </div>
                        {t2.is_demo && (
                          <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/20 ml-1 hover:bg-amber-500/10">
                            {t("staff.col.demo")}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-blue-500/10 text-blue-300 border border-blue-500/20 hover:bg-blue-500/10 uppercase text-[10px]">
                        {planLabel(t2.plan_code)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-300">
                      <div className="flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5 text-slate-500" /> {t2.member_count}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-400 text-sm">{fmt(t2.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm"
                              data-testid={`view-audit-${t2.name}`}
                              onClick={() => viewAudit(t2)}
                              className="border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white bg-transparent">
                        <ScrollText className="h-3.5 w-3.5 mr-1" /> {t("staff.openAudit")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={!!openAudit} onOpenChange={(v) => { if (!v) { setOpenAudit(null); setAuditData(null); } }}>
        <DialogContent className="max-w-4xl bg-slate-900 border-slate-800 text-slate-100" data-testid="staff-audit-dialog">
          <DialogHeader>
            <DialogTitle>{t("staff.auditFor", { tenant: openAudit?.name })}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {t("staff.chainStatus")} :
              {auditData?.chain?.ok
                ? <span className="text-green-400 ml-1"><CheckCircle2 className="h-3 w-3 inline mr-1" /> intègre ({auditData.chain.checked})</span>
                : auditData
                  ? <span className="text-red-400 ml-1"><ShieldAlert className="h-3 w-3 inline mr-1" /> altération détectée</span>
                  : <span className="text-slate-500 ml-1"> ...</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-slate-800">
            {!auditData ? (
              <div className="p-10 text-center text-slate-500 text-sm">{t("common.loading")}</div>
            ) : auditData.entries.length === 0 ? (
              <div className="p-10 text-center text-slate-500 text-sm">{t("audit.emptyState")}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-slate-500 w-14">#</TableHead>
                    <TableHead className="text-slate-500">Action</TableHead>
                    <TableHead className="text-slate-500">Acteur</TableHead>
                    <TableHead className="text-slate-500">Horodatage</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditData.entries.map((e) => (
                    <TableRow key={e.id} className="border-slate-800 hover:bg-slate-800/40">
                      <TableCell className="text-slate-500 font-mono text-xs">#{e.seq}</TableCell>
                      <TableCell><span className="font-mono text-[11px] text-slate-200">{e.action}</span></TableCell>
                      <TableCell className="text-sm text-slate-300">
                        {e.actor_email || "—"} <span className="text-[10px] uppercase text-slate-500">({e.actor_type})</span>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{fmt(e.timestamp)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
